'use client'
import { Product, Variant } from '@/payload-types'
import { useSearchParams } from 'next/navigation'
import { useMemo } from 'react'
import { useTranslations } from 'next-intl'

import { motion } from 'framer-motion'
import { CheckCircle2, AlertTriangle, XCircle } from 'lucide-react'
import { cn } from '@/utilities/cn'

type Props = {
  product: Product
}

export const StockIndicator: React.FC<Props> = ({ product }) => {
  const t = useTranslations('product')
  const searchParams = useSearchParams()

  const variants = product.variants || []

  const stockQuantity = useMemo(() => {
    if (product.enableVariants && variants.length > 0) {
      // Find if all selected options match a specific inventory?
      // In this schema, inventory seems to be on the product or the variant value.
      // Let's check if any selected variant value has inventory.
      let variantInventory: number | null = null

      variants.forEach((v) => {
        const selectedValue = searchParams.get(v.name.toLowerCase())
        if (selectedValue) {
          const option = v.values?.find((opt) => opt.value === selectedValue)
          // Note: The schema doesn't explicitly show 'inventory' on variant values in payload-types.ts
          // but the user mentioned a fix-inventory-schema.js that adds it to variants table.
          if (option && 'inventory' in option && typeof option.inventory === 'number') {
            variantInventory = option.inventory
          }
        }
      })

      if (variantInventory !== null) return variantInventory
    }
    return (product as any).inventory || 0
  }, [variants, searchParams])

  const isLowStock = stockQuantity < 10 && stockQuantity > 0
  const isOutOfStock = stockQuantity === 0 || !stockQuantity
  const isInStock = stockQuantity >= 10

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className={cn(
        'flex items-center gap-1.5 px-2.5 py-1 rounded-md text-sm font-mono font-semibold',
        isInStock && 'text-green-500 bg-green-950/30',
        isLowStock && 'text-orange-400 bg-orange-950/30',
        isOutOfStock && 'text-red-400 bg-red-950/30',
      )}
    >
      {isInStock && <CheckCircle2 className="h-4 w-4" />}
      {isLowStock && <AlertTriangle className="h-4 w-4" />}
      {isOutOfStock && <XCircle className="h-4 w-4" />}

      <span>
        {isInStock && t('inStock')}
        {isLowStock && t('lowStock')}
        {isOutOfStock && t('outOfStock')}
      </span>
    </motion.div>
  )
}
